import { formatDecimalOdds } from '../../utils/odds'
import { formatSportLabel } from '../../utils/sportLabels'
import { legLabel } from './legModel'
import styles from './LegSearch.module.css'
import { legBetLabel } from '../../../../../shared/pkg/prepacks/templateRendering.js'

function legMeta(leg, status) {
  const parts = [formatSportLabel(leg.sport), leg.league, status]
  if (leg.event_start_time) parts.push(new Date(leg.event_start_time).toLocaleString())
  return parts.filter(Boolean).join(' · ')
}

// The market, then the selection with its line ("Point Spread · KC Chiefs -6").
// Search often puts the line in the selection name already; legLabel adds it
// only when it is missing, so the line is never shown twice.
function selectionText(leg) {
  const selection = legLabel(leg)
  return [leg.market_label, selection === leg.market_label ? '' : selection].filter(Boolean).join(' · ')
}

// A picked leg as its card row reads it (#865): the participant when the leg
// names one, else the selection with its line; under it the bet and the game
// ("Bruno Fernandes" over "1+ Goals · Como - Manchester United"). A leg without
// a participant keeps its market under its selection.
function compactTitle(leg) {
  return String(leg?.participant || '').trim() || legLabel(leg)
}

function compactMeta(leg) {
  return [legBetLabel(leg), leg?.event_label].filter(Boolean).join(' · ')
}

function legOdds(leg, oddsFormat) {
  const decimal = formatDecimalOdds(leg.decimal_odds)
  return oddsFormat === 'decimal' ? decimal : (leg.odds || decimal)
}

// One leg as a search result: its event, when it plays, the selection and its
// price. `status` is the event's (upcoming or live), when the list mixes both;
// `notes` sit under the price.
//
// `compact` is the short form for legs already picked or suggested: the
// participant (else the selection) with `badge` beside it, the bet and event
// under it (compactTitle, compactMeta), the price, and `actions` (add,
// reorder, remove) at the end.
export default function LegRow({ leg, oddsFormat = 'american', status, notes = [], actions, compact = false, badge }) {
  const odds = legOdds(leg, oddsFormat)
  const positive = oddsFormat === 'american' && odds.startsWith('+')

  if (compact) {
    return (
      <span className={styles.compactRow}>
        <span className={styles.compactText}>
          <span className={styles.compactTitle}>
            <strong>{compactTitle(leg)}</strong>
            {badge}
          </span>
          <span className={styles.compactMeta}>{compactMeta(leg)}</span>
        </span>
        {odds ? <span className={`${styles.compactOdds} ${positive ? styles.positiveOdds : ''}`.trim()}>{odds}</span> : null}
        {actions ? <span className={styles.compactActions}>{actions}</span> : null}
      </span>
    )
  }

  return (
    <span className={styles.row}>
      <span className={styles.event}>{leg.event_label || 'Event'}</span>
      <span className={styles.meta}>{legMeta(leg, status)}</span>
      <span className={styles.selection}>
        {selectionText(leg)}
      </span>
      <span className={styles.numbers}>
        <strong>{odds || 'Price pending'}</strong>
        {notes.map((note) => <span key={note}>{note}</span>)}
      </span>
    </span>
  )
}
